import React, { useEffect, useState } from "react";
import { GET_PRESIGNED_URL } from "../../services/Endpoints";
import { DEFAULT_STATUS_CODE_SUCCESS } from "../../utils/AppConstants";
import { encryptData } from "../../utils/AppFunctions";
import { IMAGE_PATH } from "../../config/AppConfig";
import { localStorageUtils } from "../../utils/LocalStorageUtil";

type TICImageProps = React.DetailedHTMLProps<
  React.ImgHTMLAttributes<HTMLImageElement>,
  HTMLImageElement
> & {
  imageUrl?: string;
  fallbackSrc?: string;
  loadingClassName?: string;
  onClose?: () => void;
  fillColor?: string;
  isPrivate?: boolean;
  showOriginal?: boolean;
  containerClass?: string;
  noLoader?: boolean;
};

export const removeThumb = (url: string | undefined): string | undefined => {
  // Use a regular expression to replace 'thumb-' in the filename
  if (url) {
    return url.replace(/\/thumb-([^/]+)$/, "/$1");
  }
};

const ICImage: React.FC<TICImageProps> = ({
  imageUrl,
  fallbackSrc,
  className,
  loadingClassName,
  src,
  alt,
  onClose,
  fillColor,
  onLoad,
  isPrivate = false,
  showOriginal = false,
  noLoader = false,
  containerClass,
  ...rest
}) => {
  const [imageSrcUrl, setImageSrcUrl] = useState<string>();
  const [isLoading, setIsLoading] = useState(true);
  const token = localStorageUtils.getAccessToken();

  imageUrl = showOriginal ? removeThumb(imageUrl) : imageUrl;

  const handleImageLoad = (e: any) => {
    if (onLoad) {
      onLoad(e);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    const fetchImage = async () => {
      try {
        if (imageUrl && isPrivate) {
          const resImage = await GET_PRESIGNED_URL(encryptData(imageUrl));
          if (resImage?.code === DEFAULT_STATUS_CODE_SUCCESS) {
            setImageSrcUrl(resImage.data);
          }
        } else {
          const value = `${IMAGE_PATH}/${imageUrl}?${token ? `t=${token}` : ""}`;
          setImageSrcUrl(value);
        }
      } catch (error) {
        setImageSrcUrl(undefined);
      }
    };

    fetchImage();

    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  return imageUrl ? (
    <div
      className={`flex h-full w-full justify-center items-center relative ${
        containerClass ?? ""
      }`}
    >
      <img
        src={imageSrcUrl}
        alt={alt}
        className={`${
          isLoading && !noLoader ? "hidden" : "img-load-zoom-out"
        } ${className ?? ""}`}
        onLoad={handleImageLoad}
        {...rest}
        onError={() =>
          setImageSrcUrl(
            src ??
              fallbackSrc ??
              require("../../assets/image/no-image.svg").default,
          )
        }
      />
      {isLoading && !noLoader && (
        <div
          className={`absolute top-0 left-0 h-full w-full flex justify-center items-center ${
            className ?? ""
          }`}
        >
          <div>
            <div className={`img-loader-test`} />
          </div>
        </div>
      )}
    </div>
  ) : (
    <div
      style={{ backgroundColor: fillColor }}
      className={`flex h-full w-full justify-center items-center relative ${
        containerClass ?? ""
      }`}
    >
      <img
        {...rest}
        alt={alt}
        className={`${className ?? ""} `}
        src={
          src ??
          fallbackSrc ??
          require("../../assets/image/no-image.svg").default
        }
      />
    </div>
  );
};

export default ICImage;
